import { Button, Card, Form, FormButtonRow, PageHeader, PageLayout } from '@ksyos/design-system';
import { useParams } from '@ksyos/router';
import { useEffect, useReducer, useState } from 'react';
import type { ActivityType } from '@common/enums/';
import type { ValidationErrors } from '@frontend/utils/validation';
import { FinancialReportType, ManagerAuthorization } from '@common/enums/';
import { never } from '@common/utils/never';
import { financialExportSchema } from '@common/validation/financialExportSchema';
import { ChoiceWithEpisodeCategory } from '@common/validation/schemas';
import { getSelectedLanguage } from '@frontend/translate';
import { assertHasAuthorization, useAbortableFetch } from '@frontend/utils';
import { formatDateIntoString } from '@frontend/utils/formatDateIntoString';
import { handleValidatePeriod, validateDatePeriodSchema } from '@frontend/utils/validateDatePeriod';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '@frontend/utils/validation';
import { DownloadProgressFeedbackType, ExportDownloadProgress } from '../../../elements/ExportDownloadProgress';
import {
    aanvraag,
    analist,
    biometrie,
    consult,
    declaratie,
    factuurOrgGrp,
    factuurPatient,
    grading,
    instellingen,
    otherPreSelectedValues,
    patient,
    preSelectedValuesComplete,
    preSelectedValuesFunding,
    preSelectedValuesPayouts,
    privacySensitiveDataColums,
    proces,
    supervisie,
    uitbetaling,
} from '../dataColums';
import { ProvideReasonForAccessModal } from './ProvideReasonForAccessModal';
import { RequestFinancialExportFilters } from './RequestFinancialExportFilters/RequestFinancialExportFilters';
import { RequestFinancialExportMultiSelect } from './RequestFinancialExportMultiSelect';
import { selectedExportValuesReducer, type SelectedExportValuesStatePayload } from './requestFinancialExportReducer';
import {
    getDataColumns,
    getHeaderText,
    type IColumnData,
    type ISearchParams,
    type ISearchRequest,
} from './requestFinancialExportUtils';

const SelectedExportValuesInitialState = {
    aanvraagSelectValue: [],
    analistSelectValue: [],
    biometrieSelectValue: [],
    consultSelectValue: [],
    declaratieSelectValue: [],
    factuurOrgGrpSelectValue: [],
    factuurPatientSelectValue: [],
    gradingSelectValue: [],
    instellingenSelectValue: [],
    patientSelectValue: [],
    procesSelectValue: [],
    supervisieSelectValue: [],
    uitbetalingSelectValue: [],
};

export function RequestFinancialExport() {
    const language = getSelectedLanguage();
    const abortableFetch = useAbortableFetch();
    const { financialReportType } = useParams() as { financialReportType?: FinancialReportType };

    const [selectedExportValuesState, dispatchSelectedExportValues] = useReducer(
        selectedExportValuesReducer,
        SelectedExportValuesInitialState
    );

    const {
        procesSelectValue,
        aanvraagSelectValue,
        patientSelectValue,
        biometrieSelectValue,
        analistSelectValue,
        gradingSelectValue,
        supervisieSelectValue,
        consultSelectValue,
        factuurOrgGrpSelectValue,
        factuurPatientSelectValue,
        declaratieSelectValue,
        uitbetalingSelectValue,
        instellingenSelectValue,
    } = selectedExportValuesState;

    const [isDownloading, setIsDownloading] = useState(false);
    const [searchParams, setSearchParams] = useState<ISearchParams>({
        episodeCategoryId: null,
        serviceChoice: ChoiceWithEpisodeCategory.all,
        serviceId: null,
    });
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [exportDownloadId, setExportDownloadId] = useState<string>();
    const [showModal, setShowModal] = useState<boolean>(false);
    const [dataColumns, setDataColumns] = useState<IColumnData[]>([]);
    const [sensitiveDataColumns, setSensitiveDataColumns] = useState<string[]>([]);

    assertHasAuthorization([
        ManagerAuthorization.reportFinancialComplete,
        ManagerAuthorization.reportFunding,
        ManagerAuthorization.reportPayouts,
    ]);

    useEffect(() => {
        setDataColumns([]);
    }, [financialReportType]);

    useEffect(() => {
        if (dataColumns.length === 0) {
            loadDataColumns();
        }
        setPreselectedValues();
    }, [dataColumns]);

    return (
        <>
            <PageLayout width="medium">
                <PageHeader title={getHeaderText(financialReportType)} />
                <Card>
                    <Card.Body>
                        <Form
                            onSubmit={async () => await onSubmit()}
                            submitOnEnter={false}
                        >
                            <RequestFinancialExportFilters
                                errors={errors}
                                financialReportType={financialReportType}
                                isDisabled={isDownloading}
                                onChange={(filterName: keyof ISearchParams, value) => {
                                    handleFilterChange(filterName, value);
                                }}
                                searchParams={searchParams}
                            />
                            <RequestFinancialExportMultiSelect
                                dataColumns={dataColumns}
                                financialReportType={financialReportType}
                                onSelectionChange={handleMultiSelectChange}
                                options={(columnName) => getValueFromState(columnName)}
                            />
                            <FormButtonRow>
                                <Button
                                    isDisabled={isDownloading}
                                    onClick={onSubmit}
                                >
                                    {language.export.downloadReport}
                                </Button>
                            </FormButtonRow>

                            {isDownloading && (
                                <ExportDownloadProgress
                                    downloadProcessFeedbackType={DownloadProgressFeedbackType.spinner}
                                    exportDownloadId={exportDownloadId}
                                    onFinished={() => {
                                        setExportDownloadId(undefined);
                                        setIsDownloading(false);
                                    }}
                                />
                            )}
                        </Form>
                    </Card.Body>
                </Card>
            </PageLayout>
            {showModal && (
                <ProvideReasonForAccessModal
                    onCloseModal={() => setShowModal(false)}
                    onDownload={downloadExport}
                    sensitiveDataColumns={sensitiveDataColumns}
                />
            )}
        </>
    );

    function handleFilterChange<K extends keyof ISearchParams>(name: K, value: ISearchParams[K]) {
        let clearDropdown = {};
        if (name === 'serviceChoice' && value !== ChoiceWithEpisodeCategory.specific) {
            clearDropdown = { serviceId: null };
        }
        if (name === 'serviceChoice' && value !== ChoiceWithEpisodeCategory.episodeCategory) {
            clearDropdown = { episodeCategoryId: null };
        }

        const updatedSearchParams = { ...searchParams, ...clearDropdown, [name]: value };
        setSearchParams(updatedSearchParams);

        const validationErrors = handleValidateFilters(updatedSearchParams, name) || emptyValidationErrors;
        setErrors(validationErrors);
    }

    function handleValidateFilters<K extends keyof ISearchParams>(
        updatedSearchParams: {
            activityTypeChoice?: ActivityType | undefined;
            endDate?: Date | undefined;
            episodeCategoryId: string | null;
            serviceChoice: ChoiceWithEpisodeCategory | null;
            serviceId: string | null;
            startDate?: Date | undefined;
        },
        name: K
    ) {
        if (name === 'endDate' || name === 'startDate') {
            return handleValidatePeriod({
                datePeriod: {
                    endDate: updatedSearchParams.endDate,
                    startDate: updatedSearchParams.startDate,
                },
                errors,
                schema: validateDatePeriodSchema,
                setErrors,
            });
        }

        return updateValidationErrorsForOne(financialExportSchema, updatedSearchParams, errors, name);
    }

    function handleMultiSelectChange(columnName: string, value: string[]) {
        if (value?.length !== getValueFromState(columnName).length) {
            handleOnSelectionChange(columnName, value);
        }
    }

    function loadDataColumns() {
        const columns = [
            proces,
            aanvraag,
            analist,
            biometrie,
            consult,
            declaratie,
            factuurOrgGrp,
            factuurPatient,
            grading,
            instellingen,
            patient,
            supervisie,
            uitbetaling,
        ];
        const neededColumns = getDataColumns(financialReportType);
        if (neededColumns === undefined) {
            setDataColumns([]);

            return;
        }
        const filteredColumns: IColumnData[] = [];
        columns.forEach((table) => {
            const tableColumns = {
                data: table.data.filter((column) => neededColumns.some((neededCol) => neededCol.key === column.key)),
                name: table.name,
            };
            filteredColumns.push(tableColumns);
        });
        setDataColumns(filteredColumns);
    }

    function setPreselectedValues() {
        let payload = selectedExportValuesState;
        if (financialReportType === FinancialReportType.payouts) {
            payload = { ...payload, ...preSelectedValuesPayouts };
        } else if (financialReportType === FinancialReportType.funding) {
            payload = { ...payload, ...preSelectedValuesFunding };
        } else {
            payload = { ...payload, ...preSelectedValuesComplete };
        }
        payload = { ...payload, ...otherPreSelectedValues };

        dispatchSelectedExportValues({
            payload,
            type: 'SET_SELECTED_VALUES',
        });
    }

    async function onSubmit() {
        const newSearchParams = { ...searchParams } as ISearchRequest;
        newSearchParams.startDate = formatDateIntoString(searchParams.startDate) || undefined;
        newSearchParams.endDate = formatDateIntoString(searchParams.endDate) || undefined;

        const { errors: validationErrors } = validate(financialExportSchema, { ...newSearchParams });
        setErrors(validationErrors);

        if (validationErrors.size > 0) {
            window.scrollTo(0, 0);

            return;
        }

        const sensitiveColumns = checkSensitiveColumnsSelected();

        if (sensitiveColumns.length > 0) {
            setShowModal(true);
        } else {
            await downloadExport();
        }
    }

    async function downloadExport() {
        setIsDownloading(true);
        setShowModal(false);

        const url = '/api/requests/requestFinancialExport';
        const body = {
            activityType: searchParams.activityTypeChoice,
            episodeCategoryId:
                searchParams.serviceChoice === ChoiceWithEpisodeCategory.episodeCategory
                    ? searchParams.episodeCategoryId
                    : undefined,
            financialReportType: financialReportType ? financialReportType : FinancialReportType.complete,
            periodEndDate: formatDateIntoString(searchParams.endDate),
            periodStartDate: formatDateIntoString(searchParams.startDate),
            selectedColumns: getSelectedColumnsKeys(),
            serviceId:
                searchParams.serviceChoice === ChoiceWithEpisodeCategory.specific ? searchParams.serviceId : undefined,
        };

        const result = await abortableFetch.fetchJson(url, 'POST', body);
        setExportDownloadId(result.json.exportDownloadId);
    }

    function checkSensitiveColumnsSelected() {
        const selectedColumns = [
            ...procesSelectValue,
            ...aanvraagSelectValue,
            ...patientSelectValue,
            ...biometrieSelectValue,
            ...analistSelectValue,
            ...gradingSelectValue,
            ...supervisieSelectValue,
            ...consultSelectValue,
            ...factuurOrgGrpSelectValue,
            ...factuurPatientSelectValue,
            ...declaratieSelectValue,
            ...uitbetalingSelectValue,
            ...instellingenSelectValue,
        ];
        const sensitiveColumns = selectedColumns.filter((column) => privacySensitiveDataColums.includes(column));
        setSensitiveDataColumns(sensitiveColumns);

        return sensitiveColumns;
    }

    function getSelectedColumnsKeys() {
        const selectedColumns = [
            ...procesSelectValue,
            ...aanvraagSelectValue,
            ...patientSelectValue,
            ...biometrieSelectValue,
            ...analistSelectValue,
            ...gradingSelectValue,
            ...supervisieSelectValue,
            ...consultSelectValue,
            ...factuurOrgGrpSelectValue,
            ...factuurPatientSelectValue,
            ...declaratieSelectValue,
            ...uitbetalingSelectValue,
            ...instellingenSelectValue,
        ];

        return selectedColumns.map((selectedColumn) => {
            const result = dataColumns
                .flatMap((dataCol) => dataCol.data)
                .find((dataColumn) => dataColumn.value === selectedColumn);

            return result?.key;
        });
    }

    function getValueFromState(name: string) {
        return (
            {
                Aanvraag: aanvraagSelectValue,
                Analist: analistSelectValue,
                Biometrie: biometrieSelectValue,
                Consult: consultSelectValue,
                Declaratie: declaratieSelectValue,
                'Factuur Org/Grp': factuurOrgGrpSelectValue,
                'Factuur Patient': factuurPatientSelectValue,
                Grading: gradingSelectValue,
                Instellingen: instellingenSelectValue,
                Patient: patientSelectValue,
                Proces: procesSelectValue,
                Supervisie: supervisieSelectValue,
                Uitbetaling: uitbetalingSelectValue,
            }[name] || []
        );
    }

    function handleOnSelectionChange(name: string, value: string[]) {
        switch (name) {
            case 'Aanvraag':
                return dispatchSetValue({ aanvraagSelectValue: value });
            case 'Analist':
                return dispatchSetValue({ analistSelectValue: value });

            case 'Biometrie':
                return dispatchSetValue({ biometrieSelectValue: value });

            case 'Consult':
                return dispatchSetValue({ consultSelectValue: value });

            case 'Declaratie':
                return dispatchSetValue({ declaratieSelectValue: value });

            case 'Factuur Org/Grp':
                return dispatchSetValue({ factuurOrgGrpSelectValue: value });

            case 'Factuur Patient':
                return dispatchSetValue({ factuurPatientSelectValue: value });

            case 'Grading':
                return dispatchSetValue({ gradingSelectValue: value });

            case 'Instellingen':
                return dispatchSetValue({ instellingenSelectValue: value });

            case 'Patient':
                return dispatchSetValue({ patientSelectValue: value });

            case 'Proces':
                return dispatchSetValue({ procesSelectValue: value });

            case 'Supervisie':
                return dispatchSetValue({ supervisieSelectValue: value });

            case 'Uitbetaling':
                return dispatchSetValue({ uitbetalingSelectValue: value });

            default:
                never(name as never);
        }
    }

    function dispatchSetValue(item: Partial<SelectedExportValuesStatePayload>) {
        dispatchSelectedExportValues({
            payload: { ...selectedExportValuesState, ...item },
            type: 'SET_SELECTED_VALUES',
        });
    }
}
